import {
  conciergeRequestStates,
  conciergeRequestTerminalStates,
  customerUpdateKinds,
  customerUpdateStates,
  fulfilmentEvidenceStates,
  openRequestStates,
  requestHolderOfState,
  requestInStateCommandStates,
  requestStatesByHolder,
} from "@lafayette/domain";
import type { StaffRequestStateValue } from "@lafayette/schemas";

import type { RequestCapabilities } from "./access";
import { isOpenState } from "./labels";

// What the detail page OFFERS for one request, by state, capability and relationship. The command/state table (`docs/engineering/
// concierge-requests.md` section 2.2 and contract 7.2) is READ from the domain, not copied (CHT-2002). It is presentation only: the server plans every command again
// in its transaction and refuses a wrong one with a visible 409 or 403. Rules for this file:
//   - A state that does not allow the command: `null`. No control is drawn.
//   - A state that allows it for someone else (the owner, the designated assignee, a supervisor): the control is drawn DISABLED with
//     the reason in words.
//   - When the browser does not know who the viewer is (`viewer.staffUserId` is `null`), a relationship rule is not judged: the control
//     is offered and the server decides. The role (capability) rule is always judged.
// The table below is tested row by row, with the expected cells written by hand (`availability.test.ts`).

export type RequestAction =
  | "claim"
  | "reassign"
  | "ask"
  | "workStatus"
  | "unavailable"
  | "decline"
  | "cancel"
  | "complete"
  | "priority"
  | "statusSummary"
  | "note"
  | "confirm"
  | "recordEvidence"
  | "publishUpdate"
  | "correct"
  | "attachment"
  | "shareAttachment";

export const requestActionOrder: readonly RequestAction[] = [
  "claim",
  "reassign",
  "ask",
  "workStatus",
  "complete",
  "unavailable",
  "decline",
  "cancel",
  "priority",
  "statusSummary",
  "note",
  "confirm",
  "recordEvidence",
  "publishUpdate",
  "correct",
  "attachment",
  "shareAttachment",
];

export type Availability =
  | Readonly<{ available: true }>
  | Readonly<{ available: false; reason: string }>;

export type RequestViewer = Readonly<{
  /** The signed-in staff user's id when the host knows it, else `null` (then the server judges ownership). */
  staffUserId: string | null;
  capabilities: RequestCapabilities;
}>;

export type RequestParties = Readonly<{
  state: StaffRequestStateValue;
  owner: Readonly<{ staffUserId: string; displayName: string | null }> | null;
  assignee: Readonly<{ staffUserId: string; displayName: string | null }> | null;
}>;

const YES: Availability = { available: true };
const NO = (reason: string): Availability => ({ available: false, reason });

const who = (person: Readonly<{ displayName: string | null }> | null): string =>
  person?.displayName ?? "another staff member";

type StatesByWho = Readonly<{
  /** The supervisor only. */
  supervisor?: readonly StaffRequestStateValue[];
  /** The designated assignee, or a supervisor. */
  designated?: readonly StaffRequestStateValue[];
  /** The owner, or a supervisor. */
  owner?: readonly StaffRequestStateValue[];
}>;

/** The states in which the staff console shows a request (never a draft, never an ended one). */
const STAFF_STATES: readonly StaffRequestStateValue[] = openRequestStates;
const ENDED: readonly StaffRequestStateValue[] = conciergeRequestTerminalStates;

const holderStates = (states: readonly StaffRequestStateValue[]): Required<StatesByWho> => ({
  supervisor: states.filter((state) => requestHolderOfState(state) === "nobody"),
  designated: states.filter((state) => requestHolderOfState(state) === "designated"),
  owner: states.filter((state) => requestHolderOfState(state) === "owner"),
});

/**
 * The rows of the command/state table: which state allows the command, and for whom. The rows of a MOVE come from the transition table of the
 * domain (`requestStatesByHolder`), the rows of an in-state command from `requestInStateCommandStates`, and the rows of the evidence, the
 * attachments and the customer updates from the domain's own state lists (CHT-2002). Only the reading "who holds the request in this state" is
 * written here, and it is the domain's too (`requestHolderOfState`). `availability.test.ts` holds the result row by row, written by hand.
 */
const commandStateTable: Readonly<Record<RequestAction, StatesByWho>> = {
  claim: requestStatesByHolder("claim"),
  // "Assign" (SUBMITTED, a supervisor) and "reassign" (a held request: the supervisor in ASSIGNED, the owner in a working state).
  reassign: {
    supervisor: [
      ...requestStatesByHolder("assign").supervisor,
      ...holderStates(requestInStateCommandStates.reassign_owner).designated,
    ],
    owner: holderStates(requestInStateCommandStates.reassign_owner).owner,
  },
  ask: requestStatesByHolder("await_customer"),
  workStatus: { owner: requestInStateCommandStates.set_work_status },
  unavailable: requestStatesByHolder("mark_unavailable"),
  decline: requestStatesByHolder("decline"),
  cancel: requestStatesByHolder("cancel"),
  complete: requestStatesByHolder("complete"),
  priority: {
    owner: requestInStateCommandStates.set_priority.filter((s) => STAFF_STATES.includes(s)),
  },
  statusSummary: {
    owner: requestInStateCommandStates.update_status_summary.filter((s) =>
      STAFF_STATES.includes(s),
    ),
  },
  // Confirm (CHT-752): only the OWNER or a supervisor. A designated assignee who has not claimed the request is not the owner and cannot
  // confirm (R8, `docs/engineering/concierge-requests.md` 4.4); the transition table has no `D` for it.
  confirm: requestStatesByHolder("confirm"),
  // Evidence and attachment metadata are recorded where the domain allows evidence (`fulfilmentEvidenceStates`), by whoever holds the request.
  recordEvidence: holderStates(fulfilmentEvidenceStates),
  attachment: holderStates(fulfilmentEvidenceStates),
  // Sharing an attachment needs no particular state (4.7): whoever may work the request, also after it closed.
  shareAttachment: {
    ...holderStates(fulfilmentEvidenceStates),
    owner: [...holderStates(fulfilmentEvidenceStates).owner, ...ENDED],
  },
  // A customer update must fit the state (`customerUpdateStates`): some kind fits each of these states, none fits the others.
  publishUpdate: {
    owner: conciergeRequestStates.filter((state) =>
      customerUpdateKinds.some((kind) => customerUpdateStates[kind].includes(state)),
    ),
  },
  // A confirmation is corrected explicitly, on a CONFIRMED request (4.5).
  correct: { owner: ["CONFIRMED"] },
  // An internal note is allowed in any state, to the owner, the designated assignee, a concierge with the customer, or a supervisor.
  note: { owner: conciergeRequestStates.filter((state) => state !== "DRAFT") },
};

function capabilityOf(action: RequestAction, c: RequestCapabilities): boolean {
  switch (action) {
    case "note":
      return c.internalNotes;
    default:
      return c.work;
  }
}

export function requestActionAvailability(
  action: RequestAction,
  parties: RequestParties,
  viewer: RequestViewer,
): Availability | null {
  const row = commandStateTable[action];
  const { state } = parties;
  const bySupervisor = row.supervisor?.includes(state) === true;
  const byDesignated = row.designated?.includes(state) === true;
  const byOwner = row.owner?.includes(state) === true;
  if (!bySupervisor && !byDesignated && !byOwner) return null;
  if (!capabilityOf(action, viewer.capabilities)) {
    return NO("Your staff role does not include this action.");
  }
  const isSupervisor = viewer.capabilities.reassignOwner;
  if (isSupervisor) return YES;
  const me = viewer.staffUserId;

  if (bySupervisor) return NO("Only a supervisor can do this in the request's current state.");

  if (byDesignated) {
    // Claim is also open to a concierge with an effective assignment to the customer when nobody is named (the shared queue):
    // the browser cannot see that relationship, so the server decides.
    if (me === null || parties.assignee === null || parties.assignee.staffUserId === me) return YES;
    if (action === "claim") {
      return NO(
        `This request is held for ${who(parties.assignee)}. Only they or a supervisor can claim it.`,
      );
    }
    return NO(`Only ${who(parties.assignee)} or a supervisor can do this.`);
  }

  // owner rule
  if (action === "note") return YES;
  if (state === "SUBMITTED") {
    return NO("Nobody holds this request yet. A supervisor must assign it first.");
  }
  if (parties.owner === null) {
    return NO(
      isOpenState(state)
        ? "Claim the request first. Only the owner or a supervisor can do this."
        : "Only the owner or a supervisor can do this.",
    );
  }
  if (me === null || parties.owner.staffUserId === me) return YES;
  return NO(`Only the owner (${who(parties.owner)}) or a supervisor can do this.`);
}

export type RequestActions = Readonly<Record<RequestAction, Availability | null>>;

export function requestActions(parties: RequestParties, viewer: RequestViewer): RequestActions {
  const entries = requestActionOrder.map(
    (action) => [action, requestActionAvailability(action, parties, viewer)] as const,
  );
  return Object.fromEntries(entries) as RequestActions;
}

/** "Assign" for a request nobody holds (SUBMITTED, or a coverage gap), "Reassign" for one that has a holder. */
export function reassignLabel(state: StaffRequestStateValue, coverageGap: boolean): string {
  return state === "SUBMITTED" || coverageGap ? "Assign to a concierge" : "Reassign";
}
